import './PickupMap.css'

import { useWhen } from '@speedlo/hooks'
import { observer, useLocalStore } from 'mobx-react-lite'
import React from 'react'

import { useRoot } from '../../app/useRoot'
import { MapBranchMarker } from '../../common/MapBranchMarker'
import { MapClickableContent } from '../../components/Map/MapClickableContent'
import { MapMarker } from '../../components/Map/MapMarker'
import { useMapPanning } from '../../components/Map/useMapPanning'
import { FPickupBranchFragment } from '../../graph/generated'
import { idPropEq, RM } from '../../tools/ramda'
import { useFoodMenuQuery } from '../foodMenu/hooks/useFoodMenuQuery'
import { useOrderCreate } from './hooks/useOrderCreate'
import { usePickupMapQuery } from './hooks/usePickupMapQuery'
import { PickupMapBranch } from './PickupMapBranch'

type TProps = NoChildren & {
  onConfirm(): void
}

export const PickupMap = observer<TProps>(({ onConfirm }) => {
  const { business } = useRoot()

  const store = useLocalStore(() => ({
    activeBranchId: null as Maybe<ID>,
  }))

  // prefetch food menu for active branch
  useFoodMenuQuery(store.activeBranchId)

  const { getStash } = usePickupMapQuery()

  // select business branchId by default
  useWhen(
    () => business.branchId !== null && store.activeBranchId === null,
    () => {
      store.activeBranchId = business.branchId
    },
  )

  const { createPickupOrder } = useOrderCreate(onConfirm)

  const renderBranchMarker = (branch: FPickupBranchFragment) => (
    <MapBranchMarker
      key={`${branch.id}-marker`}
      branch={branch}
      onClick={() => {
        store.activeBranchId = branch.id
      }}
    />
  )

  const renderBranchInfo = (branch: FPickupBranchFragment) => (
    <MapMarker key={branch.id} position={branch.gps}>
      <MapClickableContent>
        <PickupMapBranch
          branch={branch}
          onClose={() => {
            store.activeBranchId = null
          }}
          onConfirm={() => {
            createPickupOrder(branch.id)
          }}
        />
      </MapClickableContent>
    </MapMarker>
  )

  const panToGps = useMapPanning()

  const stash = getStash(RM.identity)

  if (stash === null) {
    return null
  }

  if (business.isBrand && stash.brand) {
    const branches = stash.brand

    const activeBranch =
      store.activeBranchId !== null &&
      branches.find(idPropEq(store.activeBranchId))

    if (activeBranch) {
      panToGps(activeBranch.gps)
    }

    return (
      <div data-testid="PickupMap">
        {branches && branches.map(renderBranchMarker)}
        {activeBranch && renderBranchInfo(activeBranch)}
      </div>
    )
  } else if (stash.branch) {
    const branch = stash.branch
    panToGps(branch.gps)
    return (
      <div data-testid="PickupMap">
        {renderBranchMarker(branch)}
        {renderBranchInfo(branch)}
      </div>
    )
  }

  return null
})
